import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import ShareIcon from '@mui/icons-material/Share';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import { API_BASE_URL } from '../../../config';

interface Photo {
  id: string;
  original_key: string;
  title: string | null;
  in_gallery: boolean;
  created_at: string;
}

export function GalleryPage() {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPhotos, setTotalPhotos] = useState(0);
  const [totalActive, setTotalActive] = useState(0);
  const [snackOpen, setSnackOpen] = useState(false);
  const pageRef = React.useRef(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const [shareToken, setShareToken] = useState<string>('');

  const fetchPhotos = useCallback(async () => {
    // Get user ID for share link
    try {
      const tokenRes = await fetch(`${API_BASE_URL}/api/v1/users/me/share-token`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
      });
      const tokenData = await tokenRes.json();
      setShareToken(tokenData.shareToken || '');
    } catch {}
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_BASE_URL}/api/v1/photos/my-gallery?page=${pageRef.current}&limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setPhotos(prev => pageRef.current === 1 ? (data.photos || []) : [...prev, ...(data.photos || [])]);
      setHasMore(data.hasMore || false);
      if (data.total !== undefined) setTotalPhotos(data.total);
      if (data.totalActive !== undefined) setTotalActive(data.totalActive);

    } catch (err) {
      console.error('Failed to fetch gallery photos:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Reset state on mount (handles navigation back to page)
    pageRef.current = 1;
    setPhotos([]);
    setHasMore(true);
    setLoading(true);
    fetchPhotos();
  }, []);

  // Infinite scroll via window scroll
  useEffect(() => {
    const handleScroll = () => {
      if (loadingMore || !hasMore) return;
      const scrollBottom = window.innerHeight + window.scrollY;
      const docHeight = document.documentElement.scrollHeight;
      if (scrollBottom >= docHeight - 300) {
        setLoadingMore(true);
        pageRef.current += 1;
        fetchPhotos().then(() => setLoadingMore(false));
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadingMore, hasMore, fetchPhotos]);


  const toggleGallery = async (photoId: string, current: boolean) => {
    const token = localStorage.getItem('accessToken');
    try {
      await fetch(`${API_BASE_URL}/api/v1/photos/${photoId}/gallery`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ inGallery: !current }),
      });
      setPhotos((prev) =>
        prev.map((p) => (p.id === photoId ? { ...p, in_gallery: !current } : p))
      );
      setTotalActive(prev => !current ? prev + 1 : prev - 1);
    } catch (err) {
      console.error('Failed to toggle gallery:', err);
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/gallery/${shareToken}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback for HTTP localhost
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setSnackOpen(true);
  };

  

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, pb: 8 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {t('gallery.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {totalPhotos} {t('globe.photos')} • {totalActive} {t('gallery.photosActive')}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<ShareIcon />}
          onClick={handleShare}
          size="small"
          disabled={totalPhotos === 0}
        >
          {t('gallery.share')}
        </Button>
      </Box>

      {/* Privacy Warning */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <span dangerouslySetInnerHTML={{ __html: t('gallery.privacyWarning') }} />
      </Alert>

      {/* Photo Grid - Zig-zag masonry */}
      {photos.length === 0 ? (
        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <PhotoLibraryIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            {t('gallery.empty')}
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 1,
            '& > :nth-of-type(4n+1)': { gridColumn: 'span 2', gridRow: 'span 2' },
          }}
        >
          {photos.map((photo) => {
            const url = photo.url || `/uploads/${photo.original_key.split('/').pop()}`;
            return (
              <Box
                key={photo.id}
                sx={{
                  position: 'relative',
                  borderRadius: 2,
                  overflow: 'hidden',
                  aspectRatio: '1',
                  opacity: photo.in_gallery ? 1 : 0.6,
                  transition: 'opacity 0.2s',
                }}
              >
                <img
                  src={url}
                  alt={photo.title || ''}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
                {/* Toggle overlay */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                    p: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="caption" color="white" noWrap sx={{ maxWidth: '60%' }}>
                    {photo.in_gallery ? t('gallery.inGallery') : ''}
                  </Typography>
                  <Switch
                    size="small"
                    checked={photo.in_gallery}
                    onChange={() => toggleGallery(photo.id, photo.in_gallery)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4caf50' },
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Loading indicator */}
      {loadingMore && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}><CircularProgress size={24} /></Box>}

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        message={t('gallery.copied')}
      />
    </Box>
  );
}
